import { Component, OnInit } from '@angular/core';
import { MenuDataService, MenuItem } from '../../../services/menu-data.service';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { AdminService } from '../../../services/admin.service';
import {
  UserListRequest,
  UserListResponse,
} from '../../../interfaces/admin.interface';
import { AuthService } from '../../../services/auth.service';
import { ModalEnvioCorrectoComponent } from '../../../componentes/login/modal-envio-correcto/modal-envio-correcto.component';
import { environment } from '../../../../environments/environment';
import { forkJoin } from 'rxjs';
import {
  AddOrUpdatePermissionsRequest,
  AddOrUpdateRolesRequest,
  Permissions,
  AddOrUpdateStatesRequest,
} from '../../../interfaces/admin.interface';
import { GoogleAnalyticsService } from '../../../services/google-analytics/google-analytics.service';
import { ModalPermisosComponent } from '../../../componentes/modal-permisos/modal-permisos.component';
import { PermissionsService } from '../../../services/permissions/permissions.service';

/**
 * Componente para la administración de usuarios y permisos
 */
@Component({
  selector: 'app-administrador',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    ModalEnvioCorrectoComponent,
    ModalPermisosComponent,
  ],
  templateUrl: './administrador.component.html',
  styleUrl: './administrador.component.css',
})
export class AdministradorComponent implements OnInit {
  menuGroups: MenuItem[] = [];
  selectedItems: { [key: string]: boolean } = {};
  expandedMenus: { [key: string]: boolean } = {};
  showModalAgregarUsuario = false;
  usuarioForm: FormGroup;
  users: UserListResponse[] = [];
  errorMessage: string = '';
  selectedUser: any = '';
  user: any = '';
  selectedUserInput: string = '';
  success: boolean = false;
  error: boolean = false;
  confirmModal: boolean = false;
  confirmModalTitle: string = '';
  confirmModalMessage: string = '';
  selectedStatus: string = '';
  previousStatus: string = '';
  clickAdmin: boolean = false;
  clickStatus: boolean = false;
  allSelected: boolean = false;
  selectedOption: {
    menuTitle?: string;
    subMenuName?: string;
  } | null = null;

  /** Controla la visibilidad del botón para administrar permisos */
  showPermissionButton: boolean = false;
  /** Controla la visibilidad del modal de permisos */
  showPermissionModal: boolean = false;
  /** Permisos no-menú (del modal) que ya tiene asignados el usuario seleccionado */
  userModalPermissions: Permissions[] = [];

  nameType: any = [
    { id: 1, name: 'RNC' },
    { id: 2, name: 'Cédula' },
    { id: 3, name: 'Pasaporte' },
  ];

  roles: any[] = [];
  userTypeIntermediario: number = 11;
  // Constantes para las longitudes
  readonly RNC_LENGTH = 9;
  readonly CEDULA_LENGTH = 11;
  readonly PASAPORTE_LENGTH = 30;

  initialState: {
    permissions: Set<string>;
    isAdmin: boolean;
    status: string;
  } | null = null;

  /**
   * Constructor del componente
   * Inicializa los servicios necesarios y configura el formulario
   */
  constructor(
    private menuDataService: MenuDataService,
    private fb: FormBuilder,
    private adminService: AdminService,
    private authService: AuthService,
    private gAnalytics:GoogleAnalyticsService,
    private permissionsService: PermissionsService,
  ) {
    this.hasSelectedItems();
    this.usuarioForm = this.fb.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      idType: ['', Validators.required],
      idNumber: ['', Validators.required],
      phoneNumber: [''],
      mobileNumber: [''],
    });
  }

  /**
   * Carga los roles disponibles desde el servicio de administración
   * Realiza una petición HTTP para obtener los roles y los almacena en la propiedad roles
   * @returns void
   */
  loadRoles() {
    this.adminService.getRoles().subscribe({
      next: (response) => {
        this.roles = response.data;
      },
      error: (error) => {
        console.error('Error al cargar roles:', error);
        this.errorMessage = 'Error al cargar los roles.';
        this.error = true;
      },
    });
  }

  /**
   * Inicializa los items seleccionados y submenús expandidos
   */
  private initializeSelectedItems() {
    this.menuGroups.forEach((menu) => {
      this.selectedItems[menu.description] = false;
      this.expandedMenus[menu.description] = false;
      if (menu.subMenu) {
        menu.subMenu.forEach((subMenu) => {
          this.selectedItems[`${menu.description}-${subMenu.description}`] =
            false;
        });
      }
    });
  }

  /**
   * Inicializa el componente cargando usuarios y configurando listeners
   */
  ngOnInit() {
    this.loadUsers();
    this.loadRoles();

    // Agregar listener para cambios en tipo de identificación
    this.usuarioForm.get('idType')?.valueChanges.subscribe(() => {
      this.usuarioForm.get('idNumber')?.reset();
      this.usuarioForm.get('idNumber')?.updateValueAndValidity();
    });
  }

  /**
   * Carga la lista de usuarios desde el servicio
   */
  loadUsers() {
    const producerNumber = this.authService.getProducerNumber();

    if (!producerNumber) {
      this.errorMessage = 'No se pudo obtener el número de productor';
      this.users = [];
      return;
    }

    const request: UserListRequest = {
      page: 1,
      producerNumber: producerNumber,
      userType: 0,
      OrderByColumn: 'firstName',
      pageSize: Number(environment.page_size),
    };

    this.adminService.getUsers(request).subscribe({
      next: (response) => {
        if (response.data) {
          this.users = response.data.users.map((user: any) => ({
            ...user,
            idType: this.nameType.find((type: any) => type.id === user.idType)
              ?.name,
          }));

          // Si hay un usuario seleccionado, actualizar su estado
          if (this.selectedUser) {
            const currentUser = this.users.find(
              (user) => user.email === this.selectedUser.email
            );
            if (currentUser) {
              this.selectedStatus = currentUser.active ? 'activo' : 'inactivo';
              this.previousStatus = this.selectedStatus;
            }
          }
        } else {
          this.users = [];
        }
      },
      error: (error) => {
        console.error('Error al cargar usuarios:', error);
        this.errorMessage = 'Error al cargar la lista de usuarios';
        this.users = [];
      },
    });
  }

  /**
   * Alterna la expansión de un menú
   */
  toggleMenuExpansion(menuTitle: string) {
    this.expandedMenus[menuTitle] = !this.expandedMenus[menuTitle];
  }

  /**
   * Alterna la selección de un menú padre y sus submenús
   */
  toggleParentSelection(menuTitle: string, event: Event): void {
    event.stopPropagation();
    this.selectedItems[menuTitle] = !this.selectedItems[menuTitle];

    const menu = this.menuGroups.find((m) => m.description === menuTitle);
    if (menu?.subMenu) {
      menu.subMenu.forEach((sub) => {
        this.selectedItems[`${menuTitle}-${sub.description}`] =
          this.selectedItems[menuTitle];
      });
    }
    // Forzar detección de cambios
    this.selectedItems = { ...this.selectedItems };
    this.updateMenuPermissions();
  }

  /**
   * Alterna la selección de un submenú
   */
  toggleChildSelection(menuTitle: string, subMenuName: string): void {
    const key = `${menuTitle}-${subMenuName}`;
    this.selectedItems[key] = !this.selectedItems[key];

    // Obtener el menú padre
    const menu = this.menuGroups.find((m) => m.description === menuTitle);

    if (menu?.subMenu) {
      // Verificar si todos los submenús están deseleccionados
      const allSubmenusUnchecked = menu.subMenu.every(
        (sub) => !this.selectedItems[`${menuTitle}-${sub.description}`]
      );

      // Actualizar el estado del menú padre
      this.selectedItems[menuTitle] = !allSubmenusUnchecked;
    }

    // Forzar detección de cambios
    this.selectedItems = { ...this.selectedItems };
    this.updateMenuPermissions();
  }

  /**
   * Verifica si hay items seleccionados o cambios pendientes
   */
  hasSelectedItems(): boolean {
    if (!this.selectedUser || !this.initialState) {
      return false;
    }

    // Verificar cambios en permisos solo si se han modificado los checkboxes
    const currentPermissions = new Set(
      this.getSelectedPermissions().map((p) => p.ExternalId)
    );
    const permissionsChanged = !this.arePermissionsEqual(
      currentPermissions,
      this.initialState.permissions
    );

    // Verificar cambios en rol de administrador
    const adminChanged =
      (this.user.isAdmin === 'Administrador') !== this.initialState.isAdmin;

    // Verificar cambios en estado
    const statusChanged = this.selectedStatus !== this.initialState.status;

    // Solo retornar true si hay cambios en el componente que el usuario ha modificado
    if (this.clickStatus) {
      return statusChanged;
    } else if (this.clickAdmin) {
      return adminChanged;
    } else {
      return permissionsChanged || adminChanged || statusChanged;
    }
  }

  /**
   * Verifica si han cambiado los permisos del usuario
   */
  private hasPermissionsChanged(): boolean {
    if (!this.selectedUser?.permissions) {
      return Object.values(this.selectedItems).some((value) => value);
    }

    // Obtener permisos actuales
    const currentPermissions = new Set(
      this.selectedUser.permissions.map((p: any) => p.ExternalId)
    );

    // Obtener nuevos permisos seleccionados
    const newPermissions = this.getSelectedPermissions().map(
      (p) => p.ExternalId
    );

    // Comparar permisos
    const hasNewPermissions = newPermissions.some(
      (id) => !currentPermissions.has(id)
    );
    const hasRemovedPermissions = Array.from(currentPermissions).some(
      (id: any) => !newPermissions.includes(id)
    );

    return hasNewPermissions || hasRemovedPermissions;
  }

  /**
   * Verifica si han cambiado los roles del usuario
   */
  private hasRolesChanged(): boolean {
    if (!this.selectedUser?.roles) {
      return false;
    }

    const currentIsAdmin = this.selectedUser.roles.some(
      (role: any) => role.name === 'Administrador'
    );
    return currentIsAdmin !== (this.user.isAdmin === 'Administrador');
  }

  /**
   * Verifica si ha cambiado el estado del usuario
   */
  private hasStatusChanged(): boolean {
    if (!this.selectedUser) {
      return false;
    }

    const currentStatus = this.selectedUser.active ? 'activo' : 'inactivo';
    return currentStatus !== this.selectedStatus;
  }

  /**
   * Abre el modal para agregar usuario
   */
  abrirModalAgregarUsuario() {
    this.showModalAgregarUsuario = true;
  }

  /**
   * Cierra el modal de agregar usuario y limpia el formulario
   */
  cerrarModalAgregarUsuario() {
    this.showModalAgregarUsuario = false;
    this.usuarioForm.reset();
  }

  /**
   * Maneja el envío del formulario para crear usuario
   */
  onSubmitUsuario() {
    if (!this.usuarioForm.valid) {
      return;
    }

    const formValues = this.usuarioForm.value;

    const request = {
      ...formValues,
      idType: Number(formValues.idType),
      producerNumber: this.authService.getProducerNumber(),
      temporaryPassword: true,
      userType: this.userTypeIntermediario,
      Roles: [
        {
          ExternalId: this.roles.find((role) => role.name === 'Basico')
            ?.externalId,
        },
        ...(this.selectedItems['Administrador']
          ? [
              {
                ExternalId: this.roles.find(
                  (role) => role.name === 'Administrador'
                )?.externalId,
              },
            ]
          : []),
      ],
    };

    this.adminService.createUser(request).subscribe({
      next: (response) => {
        this.success = true;
        this.error = false;
        this.loadUsers();
      },
      error: (error) => {
        console.error('Error al agregar usuario:', error);
        this.error = true;

        // Manejo seguro del mensaje de error
        if (error?.error?.messages && error.error.messages.length > 0) {
          this.errorMessage = error.error.messages[0].description;
        } else if (error?.error?.message) {
          this.errorMessage = error.error.message;
        } else if (error?.message) {
          this.errorMessage = error.message;
        } else {
          this.errorMessage = error.statusCode;
        }
        this.success = false;
      },
    });
  }

  /**
   * Maneja la selección de un usuario
   */
  onUserSelect(event: any) {
    const selectedEmail = event.target.value;

    if (this.selectedUser && this.selectedUser.email === selectedEmail) {
      return;
    }

    this.selectedUser = this.users.find((user) => user.email === selectedEmail);


    if (this.selectedUser) {
      this.selectedUserInput = selectedEmail;
      this.selectedStatus = this.selectedUser.active ? 'activo' : 'inactivo';
      this.previousStatus = this.selectedStatus;

      const isAdmin = this.selectedUser.roles.some(
        (role: any) => role.name === 'Administrador'
      );

      // Obtener el menú del usuario seleccionado
      const externalId = this.selectedUser.externalId;
      const clientId = environment.client_id;

      if (externalId && clientId) {
        this.loadUserModalPermissions(externalId);

        forkJoin({
          allMenus: this.menuDataService.getMenuByUsers(clientId),
          userMenus: this.menuDataService.getMenuByUsers(clientId, externalId),
        }).subscribe({
          next: ({ allMenus, userMenus }) => {
            this.menuGroups = (allMenus.data?.menu ?? []).map(
              (item, index) => ({
                ...item,
                id: `menu_${index}_${item.description}`,
                subMenu: item.subMenu?.map((subItem, subIndex) => ({
                  ...subItem,
                  id: `submenu_${index}_${subIndex}_${subItem.description}`,
                })),
              })
            );

            this.selectedItems = {};
            this.expandedMenus = {};
            this.initializeSelectedItems();

            if (userMenus.data?.menu) {
              this.showPermissionButton = true;
              userMenus.data.menu.forEach((menuItem) => {
                const menuGroup = this.menuGroups.find(
                  (group) => group.description === menuItem.description
                );

                if (menuGroup) {
                  this.selectedItems[menuItem.description] = true;

                  if (menuItem.subMenu && menuItem.subMenu.length > 0) {
                    menuItem.subMenu.forEach((subMenuItem) => {
                      this.selectedItems[
                        `${menuItem.description}-${subMenuItem.description}`
                      ] = true;
                    });
                  }
                }
              });
            }

            this.initialState = {
              permissions: new Set(
                this.getSelectedPermissions().map((p) => p.ExternalId)
              ),
              isAdmin: isAdmin,
              status: this.selectedUser.active ? 'activo' : 'inactivo',
            };
            this.updateMenuPermissions();
          },
          error: (error) => {
            console.error('Error al cargar el menú del usuario:', error);
            this.errorMessage = 'Error al cargar los permisos del usuario';
          },
        });
      }

      this.user = {
        ...this.selectedUser,
        isAdmin: isAdmin ? 'Administrador' : '',
      };
    } else {
      this.clearUser();
    }
  }

  /**
   * Limpia todas las selecciones
   */
  private clearSelections() {
    this.selectedItems = {};
    this.expandedMenus = {};
    this.initializeSelectedItems();
    this.updateMenuPermissions();
  }

  /**
   * Limpia la información del usuario seleccionado
   */
  clearUser() {
    this.selectedUser = '';
    this.selectedUserInput = '';
    this.menuGroups = [];
    this.selectedItems = {};
    this.expandedMenus = {};
    this.user = {
      isAdmin: '',
    };
    this.selectedStatus = '';
    this.previousStatus = '';
    this.showPermissionButton = false;
    this.userModalPermissions = [];
    this.updateMenuPermissions();
  }

  /**
   * Limpia el formulario de nuevo usuario
   */
  clearNewUser() {
    this.usuarioForm.reset();
  }

  /**
   * Valida la entrada de identificación según el tipo
   */
  onIdentificacionInput(event: Event) {
    const input = event.target as HTMLInputElement;
    const tipoId = Number(this.usuarioForm.get('idType')?.value);

    switch (tipoId) {
      case 3: // Pasaporte
        input.value = input.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
        break;
      case 1: // RNC
      case 2: // Cédula
        input.value = input.value.replace(/[^0-9]/g, '').trim();
        break;
    }

    this.usuarioForm.patchValue(
      {
        idNumber: input.value,
      },
      { emitEvent: false }
    );
  }

  /**
   * Valida que solo se ingresen números
   */
  onlyNumbers(event: Event) {
    const input = event.target as HTMLInputElement;
    input.value = input.value.replace(/[^0-9]/g, '').trim();
  }

  /**
   * Obtiene la longitud máxima según el tipo de identificación
   */
  getMaxLength(): number {
    const tipoId = Number(this.usuarioForm.get('idType')?.value);
    switch (tipoId) {
      case 1:
        return this.RNC_LENGTH;
      case 2:
        return this.CEDULA_LENGTH;
      case 3:
        return this.PASAPORTE_LENGTH;
      default:
        return 50;
    }
  }

  /**
   * Obtiene la longitud mínima según el tipo de identificación
   */
  getMinLength(): number {
    const tipoId = Number(this.usuarioForm.get('idType')?.value);
    switch (tipoId) {
      case 1:
        return this.RNC_LENGTH;
      case 2:
        return this.CEDULA_LENGTH;
      case 3:
        return 1; // Para pasaporte permitimos longitud variable
      default:
        return 1;
    }
  }

  /**
   * Maneja la continuación después de una acción exitosa
   */
  onContinue() {


    this.gAnalytics.trackEvent('agregar_nuevo_usuario',{
        page:'/admin',
        action:'Agregar nuevo usuario'
      })

    this.success = false;
    this.error = false;
    this.cerrarModalAgregarUsuario();
    this.clearNewUser();
    
  }

  /**
   * Maneja el cambio de rol de administrador
   */
  toggleAdminRole(event: any): void {
    if (!this.selectedUser) return;
    event.preventDefault();

    this.confirmModal = true;
    this.clickAdmin = true;
    this.clickStatus = false;
    this.confirmModalTitle = 'Cambio de rol de usuario';
    this.confirmModalMessage =
      this.user.isAdmin === 'Administrador'
        ? '¿Está seguro que desea quitar el rol de administrador? El usuario perderá acceso a todas las opciones de administración.'
        : 'El rol de administrador permite gestionar otros usuarios y acceder a todas las opciones del menú. ¿Está seguro de que desea realizar esta acción?';
  }

  /**
   * Maneja el cambio de estado del usuario
   */
  toggleStatusChange(event: any): void {
    if (!this.selectedUser) return;
    event.preventDefault();
    this.confirmModal = true;
    this.clickStatus = true;
    this.clickAdmin = false;
    this.confirmModalTitle = 'Cambio de estado de usuario';
    this.confirmModalMessage =
      this.selectedStatus === 'activo'
        ? 'Al habilitar un usuario, podrá acceder al portal de intermediarios. ¿Está seguro de que desea realizar esta acción?'
        : 'Al inhabilitar un usuario, no podrá acceder al portal de intermediarios. ¿Está seguro de que desea realizar esta acción?';
  }

  /**
   * Maneja la confirmación de acciones en el modal
   */
  confirmModalFunction(event: any, action: string): void {
    if (action === 'Aceptar') {
      if (this.clickAdmin) {
        if (typeof this.user === 'string') {
          this.user = {};
        }

        if (this.user.isAdmin === 'Administrador') {
          this.user.isAdmin = '';

          if (this.selectedOption) {
            if (this.selectedOption.subMenuName) {
              this.selectedItems[
                `${this.selectedOption.menuTitle}-${this.selectedOption.subMenuName}`
              ] = false;
              this.toggleChildSelection(
                this.selectedOption.menuTitle || '',
                this.selectedOption.subMenuName || ''
              );
            } else {
              this.selectedItems[this.selectedOption.menuTitle || ''] = false;
              this.toggleParentSelection(
                this.selectedOption.menuTitle || '',
                event
              );
            }
          }
        } else {
          this.user.isAdmin = 'Administrador';
          this.menuGroups.forEach((menu) => {
            if (this.isMenuVisible(menu.description)) {
              this.selectedItems[menu.description] = true;
              if (menu.subMenu) {
                menu.subMenu.forEach((subMenu) => {
                  this.selectedItems[
                    `${menu.description}-${subMenu.description}`
                  ] = true;
                });
              }
            }
          });
        }
      } else if (this.clickStatus) {
        this.previousStatus = this.selectedStatus;
      }
    } else {
      if (this.clickStatus) {
        this.selectedStatus = this.previousStatus;
      }
    }

    this.updateMenuPermissions();
    this.confirmModal = false;
    this.clickAdmin = false;
    this.clickStatus = false;
    this.selectedOption = null;
  }

  /**
   * Verifica si un menú debe ser visible
   */
  isMenuVisible(description: string): boolean {
    const excludedMenus = [
      'Principal',
      'Administrador',
      'Contactos',
      'Documentos',
      'Buzón de sugerencias',
    ];
    return !excludedMenus.includes(description);
  }

  /**
   * Verifica los privilegios de administrador antes de realizar cambios
   */
  checkAdminPrivileges(
    event: Event,
    menuTitle?: string,
    subMenuName?: string
  ): void {
    if (this.user.isAdmin === 'Administrador') {
      event.preventDefault();
      this.confirmModal = true;
      this.clickAdmin = true;

      this.selectedOption = {
        menuTitle,
        subMenuName,
      };

      this.confirmModalTitle = 'Quitar rol de administrador';
      this.confirmModalMessage =
        'Para poder deshabilitar esta opción, primero debe deshabilitar el rol de administrador.';
    }
  }

  /**
   * Guarda los cambios realizados en permisos y roles
   */
  guardarCambios() {
    if (!this.selectedUser) return;

    const requests = [];
    const userEmail = this.selectedUser.email;

    // Verificar si hay cambios en permisos
    const permissionsRequest: AddOrUpdatePermissionsRequest = {
      UserExternalId: this.selectedUser.externalId,
      ClientId: environment.client_id,
      Permissions: this.mergePermissions(
        this.getSelectedPermissions(),
        this.userModalPermissions
      ),
    };

    // Verificar si hay cambios en roles
    const rolesRequest: AddOrUpdateRolesRequest = {
      UserExternalId: this.selectedUser.externalId,
      ClientId: environment.client_id,
      Roles: [
        {
          ExternalId: this.roles.find((role) => role.name === 'Basico')
            ?.externalId,
        },
        ...(this.user.isAdmin === 'Administrador'
          ? [
              {
                ExternalId: this.roles.find(
                  (role) => role.name === 'Administrador'
                )?.externalId,
              },
            ]
          : []),
      ],
    };

    // Verificar si hay cambios en el estado
    const statesRequest: AddOrUpdateStatesRequest = {
      UserExternalId: this.selectedUser.externalId,
      ClientId: environment.client_id,
      Active: this.selectedStatus === 'activo',
    };
    if (this.hasPermissionsChanged()) {
      requests.push(
        this.adminService.addOrUpdatePermissions(permissionsRequest)
      );
    }

    if (this.hasRolesChanged()) {
      requests.push(this.adminService.addOrUpdateRoles(rolesRequest));
    }

    if (this.hasStatusChanged()) {
      requests.push(this.adminService.addOrUpdateStates(statesRequest));
    }

    if (requests.length === 0) return;

    forkJoin(requests).subscribe({
      next: (response) => {
        this.gAnalytics.trackEvent('guardar_permisos',{
        page:'/admin',
        action:'Guardar permisos de usuario'
      })
        this.success = true;
        this.error = false;
        this.loadUsers();
        this.onUserSelect({
          target: { value: this.generarCorreoImposible() },
        });

        setTimeout(() => {
          this.onUserSelect({ target: { value: userEmail } });
        }, 1000);
      },
      error: (error) => {
        this.error = true;
        this.errorMessage = 'Error al guardar los cambios';
        console.error('Error:', error);
      },
    });
  }

  /**
   * Genera un correo imposible para forzar la recarga
   */
  generarCorreoImposible(): string {
    const caracteresEspeciales = '!#$%&*+-=?^_`{|}~';
    const dominiosInvalidos = ['.invalid', '.test', '.example', '.local'];

    // Generar parte local del correo con caracteres especiales
    let parteLocal = '';
    for (let i = 0; i < 10; i++) {
      parteLocal += caracteresEspeciales.charAt(
        Math.floor(Math.random() * caracteresEspeciales.length)
      );
    }

    // Agregar múltiples @ que invalidan el correo
    parteLocal += '@@@';

    // Agregar dominio inválido
    const dominioInvalido =
      dominiosInvalidos[Math.floor(Math.random() * dominiosInvalidos.length)];

    return `${parteLocal}dominio${dominioInvalido}`;
  }

  /**
   * Obtiene los permisos seleccionados actualmente
   */
  private getSelectedPermissions(): Permissions[] {
    const permissions: Permissions[] = [];

    Object.entries(this.selectedItems).forEach(([key, isSelected]) => {
      if (isSelected) {
        const [menuTitle, subMenu] = key.split('-');
        const menuGroup = this.menuGroups.find(
          (m) => m.description === menuTitle
        );

        if (subMenu) {
          const subMenuItem = menuGroup?.subMenu?.find(
            (s) => s.description === subMenu
          );
          if (subMenuItem?.permissionExternalId) {
            permissions.push({
              ExternalId: subMenuItem.permissionExternalId,
              description: `${menuTitle} - ${subMenu}`,
            });
          }
        } else if (menuGroup?.permissionExternalId) {
          permissions.push({
            ExternalId: menuGroup.permissionExternalId,
            description: menuTitle,
          });
        }
      }

    });

    return permissions;
  }

  /**
   * Carga los permisos no-menú (del modal) que ya tiene asignados el usuario
   * @param userExternalId Identificador externo del usuario seleccionado
   */
  private loadUserModalPermissions(userExternalId: string): void {
    this.permissionsService
      .getUserPermissions({
        UserExternalId: userExternalId,
        ClientId: environment.client_id,
      })
      .subscribe({
        next: (response) => {
          this.userModalPermissions = response.data.permissions
            .filter((permission) => !permission.isMenu)
            .map((permission) => ({
              ExternalId: permission.externalId,
              description: permission.description,
            }));
        },
        error: (error) => {
          console.error(
            'Error al cargar los permisos no-menú del usuario:',
            error
          );
        },
      });
  }

  /**
   * Une los permisos de menú con los no-menú eliminando duplicados por ExternalId
   * @param menuPermissions Permisos derivados de los checkboxes del menú
   * @param modalPermissions Permisos no-menú asignados por el modal
   * @returns Lista combinada de permisos sin duplicados
   */
  private mergePermissions(
    menuPermissions: Permissions[],
    modalPermissions: Permissions[]
  ): Permissions[] {
    const mergedPermissions = new Map<string, Permissions>();

    [...menuPermissions, ...modalPermissions].forEach((permission) => {
      mergedPermissions.set(permission.ExternalId, permission);
    });

    return Array.from(mergedPermissions.values());
  }

  /**
   * Sincroniza el signal de permisos del menú con los permisos seleccionados actualmente
   */
  private updateMenuPermissions(): void {
    this.adminService.menuPermissionsList.set(
      this.getSelectedPermissions().map((permission) => ({
        externalId: permission.ExternalId,
        description: permission.description,
      }))
    );
  }

  private arePermissionsEqual(setA: Set<string>, setB: Set<string>): boolean {
    if (setA.size !== setB.size) return false;

    for (const item of setA) {
      if (!setB.has(item)) return false;
    }

    for (const item of setB) {
      if (!setA.has(item)) return false;
    }

    return true;
  }

  // Solo letras y espacios
  onlyLetters(event: any) {
    const input = event.target as HTMLInputElement;
    input.value = input.value.replace(/[^a-zA-ZáéíóúÁÉÍÓÚñÑ\s]/g, '');
  }

  /**
   * Selecciona todos los menús
   */
  selectAllMenus(select: boolean = true) {
    this.allSelected = select;

    // Actualizar todos los menús y submenús con el nuevo estado
    this.menuGroups.forEach((menu) => {
      if (this.isMenuVisible(menu.description)) {
        this.selectedItems[menu.description] = select;

        if (menu.subMenu) {
          menu.subMenu.forEach((subMenu) => {
            this.selectedItems[`${menu.description}-${subMenu.description}`] =
              select;
          });
        }
      }
    });

    this.updateMenuPermissions();
    return this.allSelected;
  }

  /**
   * Abre el modal para administrar los permisos del usuario
   */
  openPermissionModal() {
    this.showPermissionModal = true;
  }

  /**
   * Cierra el modal de administración de permisos
   */
  closePermissionModal() {
    this.showPermissionModal = false;
  }
}
